import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Button, ChoiceList, TextField, Badge, Tag, InlineStack } from '@shopify/polaris';
import { COLUMN_DEFS } from '../columns';
import { DateRangeBody } from './DateRangeFilter';

/**
 * AdvancedFilters — single panel exposing every filterable column at once.
 * Two-pane layout: left rail lists columns, right pane edits the selected
 * column's filter. Apply-as-you-go: every checkbox tick / preset click
 * commits immediately to the same filterGetSet state the per-column header
 * pills drive, so the panel and the pills (and the URL) are always in sync.
 *
 * Skips columns with filterType === null (text-only / order-#-search columns).
 *
 * Props:
 *   filterGetSet : map<apiKey, { get, set }>  — same map OrdersTanStack uses
 *   choicesById  : map<colId, [{label, value}]> — dynamic + static merged
 *   activeCount  : number — count of columns with non-empty filters
 */
export default function AdvancedFilters({ filterGetSet, choicesById, activeCount = 0,
                                          colOrder, onResetAll }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Build the list of filterable columns. Order matches the user's current
  // table column order (colOrder, drag-rearrangeable from Columns picker)
  // when provided — operators expect the rail to mirror the visible left→
  // right column order. Falls back to the canonical COLUMN_DEFS order.
  // Always tail-appends any filterable columns missing from colOrder so a
  // future column added in code is still reachable in the panel.
  const filterableCols = useMemo(() => {
    const choosable = COLUMN_DEFS.filter(c =>
      c.filterType === 'choice' || c.filterType === 'date' || c.filterType === 'text'
                                 || c.filterType === 'boolean'
    );
    const byIdChoosable = new Map(choosable.map(c => [c.id, c]));

    let ordered;
    if (!Array.isArray(colOrder) || colOrder.length === 0) {
      ordered = [...choosable];
    } else {
      const byId = new Map(choosable.map(c => [c.id, c]));
      const acc = [];
      for (const id of colOrder) {
        if (byId.has(id)) { acc.push(byId.get(id)); byId.delete(id); }
      }
      // Tail: any filterable cols not in colOrder (newly-added columns + filter-
      // only cols like SKU) — keep them visible.
      ordered = [...acc, ...byId.values()];
    }

    // The Fulfill By filter is conceptually a sibling of Created (both are
    // order-date range filters), so ensure it sits directly under Created in
    // the rail regardless of the user's table column order. Reorder in-place
    // only when both are present.
    const createdIdx    = ordered.findIndex(c => c.id === 'created_at');
    const fulfillByIdx  = ordered.findIndex(c => c.id === 'fulfill_by');
    if (createdIdx !== -1 && fulfillByIdx !== -1 && fulfillByIdx !== createdIdx + 1) {
      const [fb] = ordered.splice(fulfillByIdx, 1);
      const insertAt = ordered.findIndex(c => c.id === 'created_at') + 1;
      ordered.splice(insertAt, 0, fb);
    }
    // Protect Status sits immediately after Status in the rail — both are
    // order-wide fulfillment-adjacent classifiers. Same reorder-in-place
    // guarantee as fulfill_by above so the user's column ordering doesn't
    // scatter it elsewhere.
    const statusIdx  = ordered.findIndex(c => c.id === 'status');
    const protectIdx = ordered.findIndex(c => c.id === 'protect_status');
    if (statusIdx !== -1 && protectIdx !== -1 && protectIdx !== statusIdx + 1) {
      const [ps] = ordered.splice(protectIdx, 1);
      const insertAt = ordered.findIndex(c => c.id === 'status') + 1;
      ordered.splice(insertAt, 0, ps);
    }
    // Order Risk follows Protect Status — both are order-wide fraud/coverage
    // signals users want to filter side-by-side. Same reorder-in-place.
    const protectIdx2 = ordered.findIndex(c => c.id === 'protect_status');
    const riskIdx     = ordered.findIndex(c => c.id === 'risk_level');
    if (protectIdx2 !== -1 && riskIdx !== -1 && riskIdx !== protectIdx2 + 1) {
      const [rl] = ordered.splice(riskIdx, 1);
      const insertAt = ordered.findIndex(c => c.id === 'protect_status') + 1;
      ordered.splice(insertAt, 0, rl);
    }
    return ordered;
  }, [colOrder]);

  const [selectedColId, setSelectedColId] = useState(filterableCols[0]?.id);
  const [valueSearch, setValueSearch]     = useState('');
  const [skuDraft, setSkuDraft]           = useState('');   // 2026-09-26 (Trent): text being typed before it becomes a SKU chip

  // Reset value-search when switching columns
  useEffect(() => { setValueSearch(''); }, [selectedColId]);

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selectedCol = filterableCols.find(c => c.id === selectedColId) || filterableCols[0];

  // Active-filter detection per column (for the left rail dot + global badge).
  const isColActive = (col) => {
    if (col.filterType === 'choice') {
      const v = filterGetSet[col.apiFilterKey]?.get;
      return Array.isArray(v) ? v.length > 0 : false;
    }
    if (col.filterType === 'date') {
      const v = filterGetSet[col.apiFilterKey]?.get;
      return !!(v && (v.from || v.to));
    }
    if (col.filterType === 'text') {
      const v = filterGetSet[col.apiFilterKey]?.get;
      return typeof v === 'string' && v.trim().length > 0;
    }
    if (col.filterType === 'boolean') {
      return filterGetSet[col.apiFilterKey]?.get === true;
    }
    return false;
  };

  const choicesFor = (col) => {
    if (col.id === 'club')     return choicesById.club || [];
    if (col.id === 'teams')    return choicesById.teams || [];
    if (col.id === 'crm')      return choicesById.crm || [];
    if (col.id === 'location') return choicesById.location || [];
    if (col.id === 'delivery') return choicesById.delivery || [];
    return col.choices || [];
  };

  const renderEditor = () => {
    if (!selectedCol) return null;

    if (selectedCol.filterType === 'date') {
      const slot = filterGetSet[selectedCol.apiFilterKey] || {};
      const cur  = slot.get || { from: '', to: '' };
      return (
        <div className="otaf-editor-body">
          <DateRangeBody
            from={cur.from || ''}
            to={cur.to || ''}
            onChange={({ from, to }) => slot.set && slot.set({ from, to })}
            onClear={() => slot.set && slot.set({ from: '', to: '' })}
          />
        </div>
      );
    }

    if (selectedCol.filterType === 'text') {
      const slot = filterGetSet[selectedCol.apiFilterKey] || {};
      const cur  = typeof slot.get === 'string' ? slot.get : '';
      // 2026-08-03: companion boolean checkbox for the SKU filter — narrows
      // matches to orders where the SKU is not fully picked. Only rendered
      // for the SKU column (uses the sku_unpicked filter slot). No-op
      // when the SKU text is empty (backend gates on both being set).
      const companionKey = selectedCol.id === 'sku' ? 'sku_unpicked' : null;
      const companionSlot = companionKey ? (filterGetSet[companionKey] || {}) : null;
      const companionChecked = companionSlot ? companionSlot.get === true : false;
      const companionInputId = companionKey ? `otaf-companion-${companionKey}` : null;
      // 2026-09-25 (Trent): "Exclude this SKU" flips the field to orders WITHOUT
      // the SKU. It greys out the not-fully-picked toggle, which asks how much
      // of an item is picked on orders that do not contain it.
      const excludeSlot = selectedCol.id === 'sku' ? (filterGetSet.sku_exclude || {}) : null;
      const excludeChecked = excludeSlot ? excludeSlot.get === true : false;
      // 2026-09-26 (Trent): the SKU filter takes several SKUs as chips. The
      // slot still holds one comma-separated string (URL, saved views and the
      // backend all read it); Enter or a comma turns the typed text into a chip,
      // Backspace on an empty box removes the last one.
      const isSku = selectedCol.id === 'sku';
      const chips = isSku ? cur.split(',').map(x => x.trim()).filter(Boolean) : [];
      const setChips = (list) => slot.set && slot.set(list.join(', '));
      const commitDraft = () => {
        const v = skuDraft.trim().replace(/,+$/, '').trim();
        if (v && !chips.some(c => c.toLowerCase() === v.toLowerCase())) setChips([...chips, v]);
        setSkuDraft('');
      };
      return (
        <div className="otaf-editor-body">
          {isSku ? (
            <div onKeyDownCapture={(e) => {
                   if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commitDraft(); }
                   else if (e.key === 'Backspace' && !skuDraft && chips.length) { e.preventDefault(); setChips(chips.slice(0, -1)); }
                 }}>
              <TextField
                label={selectedCol.label}
                labelHidden
                placeholder={chips.length ? 'Add another SKU…' : 'Type a SKU and press Enter (substring match — e.g. PUMA, 704917)'}
                value={skuDraft}
                onChange={(v) => { if (v.includes(',')) { const parts = v.split(','); const done = parts.slice(0, -1).map(x => x.trim()).filter(Boolean); if (done.length) setChips([...chips, ...done.filter(d => !chips.some(c => c.toLowerCase() === d.toLowerCase()))]); setSkuDraft(parts[parts.length - 1]); } else setSkuDraft(v); }}
                onBlur={commitDraft}
                clearButton
                onClearButtonClick={() => { setSkuDraft(''); setChips([]); }}
                autoComplete="off"
                helpText={chips.length > 1
                  ? (excludeChecked ? 'Shows orders that have none of these SKUs.' : 'Shows orders that have any of these SKUs. Case-insensitive substring match on every line item.')
                  : 'Matches any line item on the order — parents, embellishments, optional add-ons. Case-insensitive substring. Press Enter to add more than one SKU.'}
              />
              {chips.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <InlineStack gap="150" wrap>
                    {chips.map(c => <Tag key={c} onRemove={() => setChips(chips.filter(x => x !== c))}>{c}</Tag>)}
                  </InlineStack>
                </div>
              )}
            </div>
          ) : (
            <TextField
              label={selectedCol.label}
              labelHidden
              placeholder={selectedCol.placeholder
                || `Type any ${(selectedCol.label || '').toLowerCase()} (substring match — e.g. PUMA, 704917)`}
              value={cur}
              onChange={(v) => slot.set && slot.set(v)}
              clearButton
              onClearButtonClick={() => slot.set && slot.set('')}
              autoComplete="off"
              helpText="Matches any line item on the order — parents, embellishments, optional add-ons. Case-insensitive substring."
            />
          )}
          {excludeSlot && (
            <label htmlFor="otaf-companion-sku_exclude"
                   style={{ display: 'flex', alignItems: 'flex-start', gap: 8,
                            marginTop: 12, padding: '4px 2px', cursor: 'pointer' }}>
              <input id="otaf-companion-sku_exclude" type="checkbox" checked={excludeChecked}
                     onChange={(e) => {
                       const on = e.target.checked;
                       excludeSlot.set && excludeSlot.set(on);
                       // The two are mutually exclusive; drop the toggle rather
                       // than leave it set-but-greyed and silently in the URL.
                       if (on && companionSlot && companionSlot.set) companionSlot.set(false);
                     }}
                     style={{ marginTop: 3 }} />
              <span style={{ fontSize: 14, lineHeight: 1.4 }}>
                <strong>Exclude</strong> {chips.length > 1 ? 'these SKUs' : 'this SKU'} — show orders <strong>without</strong> {chips.length > 1 ? 'any of them' : 'it'}
                <div style={{ fontSize: 12, color: '#6d7175', marginTop: 2 }}>
                  Same substring match, opposite sense. Requires a SKU above.
                </div>
              </span>
            </label>
          )}
          {companionKey && (
            <label htmlFor={companionInputId}
                   style={{ display: 'flex', alignItems: 'flex-start', gap: 8,
                            marginTop: 12, padding: '4px 2px',
                            cursor: excludeChecked ? 'not-allowed' : 'pointer',
                            opacity: excludeChecked ? 0.45 : 1 }}>
              <input id={companionInputId} type="checkbox" checked={companionChecked}
                     disabled={excludeChecked}
                     onChange={(e) => companionSlot.set && companionSlot.set(e.target.checked)}
                     style={{ marginTop: 3 }} />
              <span style={{ fontSize: 14, lineHeight: 1.4 }}>
                Only show orders where {chips.length > 1 ? 'one of these SKUs' : 'this SKU'} is <strong>not fully picked</strong>
                <div style={{ fontSize: 12, color: '#6d7175', marginTop: 2 }}>
                  {excludeChecked
                    ? 'Not available while the SKU is excluded — these orders do not have the item.'
                    : 'Includes partial picks (2 ordered, 1 scanned) and un-picked orders. Requires a SKU above.'}
                </div>
              </span>
            </label>
          )}
        </div>
      );
    }

    if (selectedCol.filterType === 'boolean') {
      const slot = filterGetSet[selectedCol.apiFilterKey] || {};
      const cur  = slot.get === true;
      const inputId = `otaf-bool-${selectedCol.id}`;
      return (
        <div className="otaf-editor-body">
          <label htmlFor={inputId}
                 style={{ display: 'flex', alignItems: 'flex-start', gap: 8,
                          padding: '8px 4px', cursor: 'pointer' }}>
            <input id={inputId} type="checkbox" checked={cur}
                   onChange={(e) => slot.set && slot.set(e.target.checked)}
                   style={{ marginTop: 3 }} />
            <span style={{ fontSize: 14, lineHeight: 1.4 }}>
              {selectedCol.booleanCheckboxLabel || `Enable "${selectedCol.label}" filter`}
            </span>
          </label>
        </div>
      );
    }

    // choice
    const slot     = filterGetSet[selectedCol.apiFilterKey] || {};
    const selected = Array.isArray(slot.get) ? slot.get : [];
    const allChoices = choicesFor(selectedCol);
    const q = valueSearch.trim().toLowerCase();
    const filtered = q
      ? allChoices.filter(c => {
          // JSX labels (delivery column) carry a `searchValue` for filter
          // matching; plain-string labels match directly.
          const text = (typeof c.label === 'string' ? c.label : (c.searchValue || c.value || ''));
          return String(text).toLowerCase().includes(q);
        })
      : allChoices;

    return (
      <div className="otaf-editor-body">
        <div className="otaf-list-actions">
          {selectedCol.searchable && (
            <div className="otaf-search-wrap">
              <TextField
                label="Search values"
                labelHidden
                placeholder="Search values…"
                value={valueSearch}
                onChange={setValueSearch}
                clearButton
                onClearButtonClick={() => setValueSearch('')}
                autoComplete="off"
              />
            </div>
          )}
          {/* 2026-09-14 (Eric): select everything the search currently shows, on top of
              what is already selected — Club, Team, Store Status, Delivery, every
              choice filter alike. Without a search it selects every option. */}
          {(() => {
            const selectedSet = new Set(selected);
            const addable = filtered.map(c => c.value).filter(v => !selectedSet.has(v));
            return (
              <Button
                size="slim"
                disabled={addable.length === 0}
                onClick={() => slot.set && slot.set([...selected, ...addable])}
                title={q ? `Select the ${filtered.length} option${filtered.length === 1 ? '' : 's'} matching "${valueSearch.trim()}"`
                         : 'Select every option'}
              >
                {q ? `Select all ${filtered.length} matching` : 'Select all'}
              </Button>
            );
          })()}
          <Button
            size="slim"
            disabled={selected.length === 0}
            onClick={() => slot.set && slot.set([])}
          >
            Deselect all
          </Button>
          <Button
            size="slim"
            disabled={allChoices.length === 0}
            onClick={() => {
              // Select Inverse: replaces current selection with everything that
              // ISN'T currently selected. Operates on the full choice list, not
              // the search-filtered subset, so a stale `valueSearch` text doesn't
              // silently shrink the inverse to "everything matching that text".
              if (!slot.set) return;
              const selectedSet = new Set(selected);
              const inverse = allChoices.map(c => c.value).filter(v => !selectedSet.has(v));
              slot.set(inverse);
            }}
            title="Replace current selection with everything that isn't selected"
          >
            Select Inverse
          </Button>
        </div>
        <div className="otaf-choice-list">
          {filtered.length === 0 ? (
            <div className="otaf-empty">No options match.</div>
          ) : (
            <ChoiceList
              titleHidden
              title="Filter"
              allowMultiple
              choices={filtered}
              selected={selected}
              onChange={(vals) => slot.set && slot.set(vals)}
            />
          )}
        </div>
      </div>
    );
  };

  const headerCount = (() => {
    if (!selectedCol) return null;
    if (selectedCol.filterType === 'choice') {
      const slot = filterGetSet[selectedCol.apiFilterKey] || {};
      const sel  = Array.isArray(slot.get) ? slot.get.length : 0;
      const total = choicesFor(selectedCol).length;
      return total > 0 ? `${sel} of ${total} selected` : '';
    }
    if (selectedCol.filterType === 'date') {
      const slot = filterGetSet[selectedCol.apiFilterKey] || {};
      const cur  = slot.get || {};
      if (cur.from && cur.to) return `${cur.from} → ${cur.to}`;
      if (cur.from) return `from ${cur.from}`;
      if (cur.to)   return `until ${cur.to}`;
      return '—';
    }
    if (selectedCol.filterType === 'text') {
      const slot = filterGetSet[selectedCol.apiFilterKey] || {};
      const cur  = typeof slot.get === 'string' ? slot.get.trim() : '';
      return cur ? `"${cur}"` : '—';
    }
    if (selectedCol.filterType === 'boolean') {
      return filterGetSet[selectedCol.apiFilterKey]?.get === true ? 'on' : 'off';
    }
    return '';
  })();

  return (
    <div className="otaf-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`otaf-trigger${open ? ' otaf-trigger--open' : ''}${activeCount > 0 ? ' otaf-trigger--active' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M2 3h12M4 8h8M6 13h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <span>Filters</span>
        {activeCount > 0 && (
          <span className="otaf-trigger-badge">
            <Badge tone="info">{String(activeCount)}</Badge>
          </span>
        )}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"
             style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
          <path d="M1 3l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="otaf-panel" role="dialog" aria-label="Advanced filters">
          {/* Top header row spanning both panes — title on the left,
              global Reset Filters action on the right. The button is
              greyed when no filters are active so accidental clicks
              are visibly no-ops. Clears every filter type at once via
              the parent's clearAllFilters callback. */}
          <div className="otaf-header">
            <span className="otaf-header-title">
              Filters
              {activeCount > 0 && (
                <span className="otaf-header-count">
                  <Badge tone="info">{String(activeCount)}</Badge>
                </span>
              )}
            </span>
            <Button
              size="slim"
              disabled={!onResetAll || activeCount === 0}
              onClick={() => { if (onResetAll) onResetAll(); }}
            >
              Reset Filters
            </Button>
          </div>
          <div className="otaf-body">
          {/* Left rail — column list */}
          <div className="otaf-rail">
            {filterableCols.map(col => (
              <button
                key={col.id}
                type="button"
                className={`otaf-rail-item${col.id === selectedCol?.id ? ' otaf-rail-item--active' : ''}`}
                onClick={() => setSelectedColId(col.id)}
              >
                <span className="otaf-rail-label">
                  {col.filterLabel || col.label}
                  {isColActive(col) && <span className="otaf-rail-dot" aria-label="active" />}
                </span>
              </button>
            ))}
          </div>
          {/* Right pane — editor */}
          <div className="otaf-editor">
            <div className="otaf-editor-header">
              <span className="otaf-editor-title">{selectedCol?.filterLabel || selectedCol?.label}</span>
              <span className="otaf-editor-count">{headerCount}</span>
            </div>
            {renderEditor()}
            <div className="otaf-editor-footer">
              <Button onClick={() => setOpen(false)}>Close</Button>
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
